import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { isHybridMode } from '../../utils/config';
import { currentLocale } from '../../i18n';
import SteamAvatar from '../common/SteamAvatar';
import '../matches/MatchComments.css';

/**
 * Comment section for a news post. Lets authenticated users post, view and
 * delete comments. Mirrors TournamentComments and reuses the MatchComments CSS.
 *
 * @param {Object} props - Component props.
 * @param {string} props.newsId - The news post id to load comments for.
 * @returns {JSX.Element} The comment section.
 */
const NewsComments = ({ newsId }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const { user, isLoggedIn, getAuthHeaders } = useAuth();

  useEffect(() => {
    if (!isHybridMode || !isLoggedIn) return;
    fetch(`${API_URL}/api/web/my-permissions`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setIsOwner(!!data?.is_owner || !!data?.is_moderator))
      .catch(() => {});
  }, [isLoggedIn, getAuthHeaders]);

  /** Fetches comments for the current news post from the API. */
  const loadComments = useCallback(() => {
    if (!newsId) return;
    fetch(`${API_URL}/api/news-comments/${newsId}`)
      .then(r => r.json())
      .then(d => setComments(d.comments || []))
      .catch(() => {});
  }, [newsId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const mySteamId = user?.steam_id || '';
  const myName = user?.steam_username || user?.discord_username || user?.username || '';

  /** Submits a new comment to the API and refreshes the list. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !mySteamId) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/news-comments/${newsId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          steam_id: mySteamId,
          player_name: myName,
          comment: newComment.trim(),
        }),
      });
      if (res.ok) {
        setNewComment('');
        loadComments();
      }
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Deletes a comment by ID.
   * @param {string} commentId - The comment ID to delete.
   */
  const handleDelete = async (commentId) => {
    try {
      await fetch(`${API_URL}/api/news-comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steam_id: mySteamId, force: isOwner }),
      });
      loadComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  /**
   * Formats an ISO date string to Swedish locale.
   * @param {string} isoStr - ISO 8601 date string.
   * @returns {string} Formatted date and time string.
   */
  const formatDate = (isoStr) => {
    const d = new Date(isoStr + 'Z');
    const loc = currentLocale();
    return d.toLocaleDateString(loc) + ' ' + d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="match-comments">
      <div className="comments-header">
        <h3>{t('comments.title')}</h3>
        <span className="comments-count">{comments.length}</span>
      </div>

      {comments.length > 0 && (
        <div className="comments-list">
          {comments.map(c => (
            <div key={c.id} className="comment-item">
              <SteamAvatar steamid64={c.steam_id} name={c.player_name} size="small" />
              <div className="comment-content">
                <div className="comment-meta">
                  <span className="comment-author">{c.player_name}</span>
                  <span className="comment-date">{formatDate(c.created_at)}</span>
                  {(mySteamId === c.steam_id || isOwner) && (
                    <button
                      className="comment-delete"
                      onClick={() => handleDelete(c.id)}
                      title={t('comments.delete')}
                    >
                      ×
                    </button>
                  )}
                </div>
                <p className="comment-text">{c.comment}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {comments.length === 0 && (
        <div className="comments-empty">{t('comments.empty')}</div>
      )}

      {isLoggedIn && mySteamId ? (
        <form className="comment-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="comment-input"
            placeholder={t('comments.placeholder')}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            maxLength={500}
            disabled={submitting}
          />
          <button
            type="submit"
            className="comment-submit"
            disabled={submitting || !newComment.trim()}
          >
            {t('queue.send')}
          </button>
        </form>
      ) : (
        <div className="comments-login-hint">{t('comments.loginHint')}</div>
      )}
    </div>
  );
};

export default NewsComments;
